import { useCallback, useEffect, useState } from 'react'
import { errorMessage, fetchAttemptAudio, listAttempts, reanalyzeAttempt, scoreAttempt } from '../../services/admin'
import { faDigits } from '../../lib/numerals'
import { ATTEMPT_STATUS_CLS, ATTEMPT_STATUS_FA } from './constants'
import { confirmed, manualScoreQuestion } from './confirm'
import { formatDatetime } from './dates'
import { FAILED_LIMIT, parseManualScore, refusalMessage } from './reviewQueue'

// ─── Review queue tab (Plan B, V9-p) ──────────────────────────────────────────
//
// What `planB.enabled` fills up: takes the backend stored instead of sending
// to Azure (`queued`), plus the ones that ran out of tries (`failed`). Both
// are dead ends until somebody acts on them, which is what this tab is for —
// listen to the recording, give it an overall score by hand, or push it back
// into the queue for Azure to try again.
//
// The two lists are two calls to `admin-attempts_list` with `status=`; the
// route has no combined filter, and doing it client-side (fetch everything,
// filter here) would pull every scored take over the wire for nothing.

const btnPrimary = 'rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50'
const btnGhost = 'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50'
const inputCls = 'rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none'

/** What went wrong, preferring this task's own wording over the server's. */
function failureText(error) {
  return refusalMessage(error) ?? errorMessage(error, 'انجام نشد.')
}

/**
 * The recording, fetched on demand. `admin-attempts_audio` is guarded, so
 * unlike the onboarding samples this cannot be a plain `<audio src>`: the
 * bytes come through the admin client and become an object URL, revoked when
 * the row goes away. Fetched only on a click — a queue of fifty rows must
 * not pull fifty recordings the moment the tab opens.
 */
export function AttemptAudio({ attempt }) {
  const [url, setUrl] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])

  async function load() {
    setBusy(true); setErr('')
    try { setUrl(URL.createObjectURL(await fetchAttemptAudio(attempt.id))) }
    catch (ex) { setErr(failureText(ex)) }
    finally { setBusy(false) }
  }

  if (!attempt.userAudioUrl) {
    return <p className="text-xs text-slate-400">ضبطی در دسترس نیست — یا پاک شده، یا بارگذاری‌اش کامل نشده.</p>
  }

  return (
    <div className="space-y-1">
      {url
        ? <audio controls src={url} className="w-full" />
        : (
          <button type="button" onClick={load} disabled={busy} className={btnGhost}>
            {busy ? 'در حال گرفتن صدا…' : '▶ پخش ضبط'}
          </button>
        )}
      {err && <p className="text-xs text-red-600">{err}</p>}
    </div>
  )
}

/**
 * The manual score: one overall number, 0–100, typed — so Latin digits, per
 * the numerals rule — plus an optional note that ends up on the audit row.
 * The warning above the form is not decoration: the backend writes this one
 * number as every sub-score, so the learner's result page has no word-by-word
 * breakdown for this take, and there is no undo — a score is credited once,
 * which is why the number is asked about once more before it is sent.
 */
export function ManualScoreForm({ attempt, onScored }) {
  const [score, setScore] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function submit(e) {
    e.preventDefault()
    let overall
    try { overall = parseManualScore(score) }
    catch (ex) { setErr(ex.message); return }
    if (!confirmed(manualScoreQuestion(overall))) return
    setBusy(true); setErr('')
    try { onScored(await scoreAttempt(attempt.id, overall, note.trim())) }
    catch (ex) { setErr(failureText(ex)) }
    finally { setBusy(false) }
  }

  return (
    <form onSubmit={submit} className="space-y-2 rounded-lg bg-slate-50 px-3 py-3">
      <p className="text-xs leading-6 text-amber-800">
        امتیاز دستی جزئیات کلمه‌به‌کلمه ندارد: همین یک عدد به‌جای همهٔ زیرامتیازها (دقت، روانی، تکمیل، آهنگ) ثبت می‌شود و
        صفحهٔ نتیجهٔ زبان‌آموز برای این تلاش هیچ تفکیکی از کلمه‌ها نشان نمی‌دهد. امتیاز فقط یک‌بار ثبت می‌شود و برگشت‌پذیر نیست.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">امتیاز کلی (۰ تا ۱۰۰)</span>
          <input type="number" min="0" max="100" step="1" value={score} onChange={e => setScore(e.target.value)}
            className={`w-28 ${inputCls}`} dir="ltr" />
        </label>
        <label className="block min-w-48 flex-1">
          <span className="mb-1 block text-xs font-medium text-slate-600">یادداشت (اختیاری، در گزارش ممیزی می‌ماند)</span>
          <input type="text" maxLength={500} value={note} onChange={e => setNote(e.target.value)}
            placeholder="مثلاً: واضح، یک کلمه نامفهوم." className={`w-full ${inputCls}`} />
        </label>
        <button type="submit" disabled={busy} className={btnPrimary}>
          {busy ? 'در حال ثبت…' : 'ثبت امتیاز دستی'}
        </button>
      </div>
      {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
    </form>
  )
}

/** Tries so far and when the job may try again — the two numbers that say whether a row is stuck. */
function TryState({ attempt }) {
  return (
    <div className="text-xs text-slate-500">
      <span>{faDigits(attempt.analysisTries)} تلاش تحلیل</span>
      <span className="mx-2 text-slate-300">·</span>
      <span>
        {attempt.nextTryAt
          ? `تلاش بعدی: ${formatDatetime(attempt.nextTryAt)}`
          : 'تلاش خودکار بعدی ندارد'}
      </span>
    </div>
  )
}

/**
 * One take waiting for a decision: who recorded it, on which sentence, when,
 * how many tries it has had, its recording, and the two ways out — a manual
 * score, or another pass at Azure.
 */
export function ReviewRow({ attempt, onChanged }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState('')

  async function requeue() {
    setBusy(true); setErr(''); setDone('')
    try {
      onChanged(await reanalyzeAttempt(attempt.id))
      setDone('به ابتدای صف برگشت؛ کار خودکار وقتی planB.enabled خاموش باشد سراغش می‌رود.')
    } catch (ex) { setErr(failureText(ex)) }
    finally { setBusy(false) }
  }

  function scored(updated) {
    onChanged(updated)
    setDone('امتیاز دستی ثبت شد.')
  }

  return (
    <div className="space-y-2 rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ATTEMPT_STATUS_CLS[attempt.status]}`}>
          {ATTEMPT_STATUS_FA[attempt.status]}
        </span>
        <span className="text-sm text-slate-700" dir="ltr">{attempt.userPhone}</span>
        <span className="ms-auto text-xs text-slate-400">ضبط: {formatDatetime(attempt.createdAt)}</span>
      </div>

      <div className="text-slate-700">
        <div className="text-sm font-medium" dir="ltr">{attempt.lessonTitleEn}</div>
        <div className="text-xs text-slate-500" dir="ltr">#{faDigits(attempt.sentenceIndex + 1)} — {attempt.sentenceTextEn}</div>
      </div>

      <TryState attempt={attempt} />

      {attempt.errorMessage && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">آخرین خطا: {attempt.errorMessage}</p>
      )}

      <AttemptAudio attempt={attempt} />

      {attempt.status === 'scored' ? (
        <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
          امتیاز کلی {faDigits(attempt.overall)}
          {attempt.scoreSource === 'manual' ? ' — دستی، بدون جزئیات کلمه‌به‌کلمه' : ' — از Azure'}
        </p>
      ) : (
        <>
          <ManualScoreForm attempt={attempt} onScored={scored} />
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={requeue} disabled={busy} className={btnGhost}>
              {busy ? '…' : 'تحلیل دوباره'}
            </button>
            <span className="text-xs text-slate-400">به‌جای امتیاز دستی، تلاش را دوباره به Azure بسپار.</span>
          </div>
        </>
      )}

      {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {done && <div className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">{done}</div>}
    </div>
  )
}

/** The two sections, given their rows — what the tab renders once loaded, and what the test renders directly. */
export function ReviewQueueList({ queued, failed, onChanged }) {
  return (
    <div className="space-y-6">
      <p className="text-xs leading-6 text-slate-500">
        این صف را کلید <code dir="ltr">planB.enabled</code> پر می‌کند: تا روشن است، ضبط‌های پذیرفته‌شده به‌جای Azure اینجا می‌نشینند و
        کار خودکار به آن‌ها دست نمی‌زند. با خاموش‌کردن آن کلید (تب «تنظیمات») صف خودبه‌خود خالی می‌شود؛ تا آن وقت هر تلاش یا
        امتیاز دستی می‌گیرد، یا دستی به صف برمی‌گردد.
      </p>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-700">در صف بررسی ({faDigits(queued.length)})</h3>
        {queued.length === 0
          ? <p className="py-8 text-center text-sm text-slate-400">صف خالی است.</p>
          : queued.map(a => <ReviewRow key={a.id} attempt={a} onChanged={onChanged} />)}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-700">ناموفق‌های اخیر ({faDigits(failed.length)})</h3>
        <p className="text-xs text-slate-500">
          تلاش‌هایی که بودجهٔ <code dir="ltr">planB.maxTries</code> را تمام کرده‌اند و به زبان‌آموز گفته شده دوباره ضبط کند؛ هنوز می‌توان امتیاز دستی داد یا دوباره به صف فرستاد.
        </p>
        {failed.length === 0
          ? <p className="py-8 text-center text-sm text-slate-400">تلاش ناموفقی نیست.</p>
          : failed.map(a => <ReviewRow key={a.id} attempt={a} onChanged={onChanged} />)}
      </section>
    </div>
  )
}

/**
 * The review queue tab (V9-p): the `queued` takes and the recent `failed`
 * ones, each with its recording, a manual score form and a re-analyse button.
 */
export function ReviewQueueTab() {
  const [queued, setQueued] = useState(null)
  const [failed, setFailed] = useState([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    setLoading(true); setErr('')
    try {
      const [q, f] = await Promise.all([
        listAttempts({ status: 'queued', limit: 200 }),
        listAttempts({ status: 'failed', limit: FAILED_LIMIT }),
      ])
      setQueued(q)
      setFailed(f)
    } catch (ex) { setErr(errorMessage(ex)) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  // A row that was just scored or re-queued keeps its place with its new
  // state, rather than vanishing under the admin's cursor; «تازه‌سازی» is
  // what actually re-draws the two lists.
  function changed(updated) {
    const replace = list => list.map(a => a.id === updated.id ? updated : a)
    setQueued(list => (list ? replace(list) : list))
    setFailed(replace)
  }

  if (err) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{err}</div>
        <button type="button" onClick={load} className={btnGhost}>تلاش دوباره</button>
      </div>
    )
  }
  if (queued === null) return <p className="py-16 text-center text-sm text-slate-400">در حال بارگذاری…</p>

  return (
    <div className="space-y-3">
      <div>
        <button type="button" onClick={load} disabled={loading} className={btnGhost}>
          {loading ? '…' : '↻ تازه‌سازی'}
        </button>
      </div>
      <ReviewQueueList queued={queued} failed={failed} onChanged={changed} />
    </div>
  )
}
